import { useState } from "react";
import { useParams } from "react-router-dom";
import { Kpi } from "../components/Kpi";
import { Badge } from "../components/Badge";
import { EditTenantIdentityModal } from "../components/EditTenantIdentityModal";
import { LegalIdentityPill } from "../components/LegalIdentityPill";
import { useTenant } from "../hooks/useTenant";
import { useViewport } from "../hooks/useViewport";
import { formatDate } from "../lib/format";
import { supabase } from "../lib/supabase";
import { colors, fontSize, fontWeight, radius, shadow, space } from "../lib/tokens";
import { missingTenantIdentityFields } from "@klubora/shared";

interface RepairResult {
  ok?: boolean;
  host?: string;
  attached?: true | "already";
  attempts?: number;
  error?: string;
  detail?: string;
  kind?: string;
  status?: number | null;
}

export function TenantOverviewPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const { tenant, events, memberCount, loading, error, refetch } = useTenant(slug);
  const vp = useViewport();
  const isMobile = vp === "mobile";
  const [editing, setEditing] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState<RepairResult | null>(null);

  const handleRepairDomain = async () => {
    if (!tenant) return;
    setRepairing(true);
    setRepairResult(null);
    const { data, error: fnErr } = await supabase.functions.invoke<RepairResult>(
      "platform-repair-tenant-domain",
      { body: { tenant_id: tenant.id } },
    );
    setRepairing(false);
    if (fnErr) {
      setRepairResult({ error: fnErr.message });
      return;
    }
    setRepairResult(data ?? { error: "no_response" });
  };

  if (loading) return <div style={{ color: colors.stone }}>Carregando...</div>;
  if (error || !tenant) return <div style={{ color: colors.error }}>Tenant não encontrado.</div>;

  // A MESMA lista que as duas edge functions do platform exigem — importada,
  // não redigitada. Uma cópia local diria "completo" sobre um cadastro que o
  // save recusa.
  const legalMissing = missingTenantIdentityFields(tenant);

  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: space.lg, marginBottom: space.xxxl }}>
        <Kpi label="Membros ativos" value={memberCount?.toLocaleString("pt-BR") ?? "—"} />
        <Kpi label="Lifecycle" value={tenant.lifecycle_state} />
        <Kpi label="Locale" value={tenant.locale} sub={tenant.timezone} />
        <Kpi label="Currency" value={tenant.currency} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 24, alignItems: "start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold }}>Identidade</div>
            <button onClick={() => setEditing(true)} style={editButton}>Editar</button>
          </div>
          <div style={{ background: colors.white, border: `1px solid ${colors.fog}`, borderRadius: radius.md, boxShadow: shadow.sm, padding: 20 }}>
            <dl style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "200px 1fr", rowGap: isMobile ? 4 : 12, columnGap: 16, fontSize: fontSize.md }}>
              <dt style={{ color: colors.stone }}>Slug</dt><dd style={{ marginBottom: isMobile ? 8 : 0 }}><code style={{ fontFamily: "JetBrains Mono", fontSize: 12 }}>{tenant.slug}</code></dd>
              <dt style={{ color: colors.stone }}>Domínio admin</dt><dd style={{ marginBottom: isMobile ? 8 : 0 }}>{tenant.admin_domain ?? "—"}</dd>
              <dt style={{ color: colors.stone }}>Email de contato</dt><dd style={{ marginBottom: isMobile ? 8 : 0 }}>{tenant.support_email ?? "—"}</dd>
              <dt style={{ color: colors.stone }}>WhatsApp suporte</dt><dd style={{ marginBottom: isMobile ? 8 : 0 }}>{tenant.support_whatsapp ?? "—"}</dd>
              <dt style={{ color: colors.stone }}>Locale · Timezone</dt><dd>{tenant.locale} · {tenant.timezone}</dd>
            </dl>

            {/* KLUB-185 — o cadastro jurídico num bloco só, com o estado à
                vista. Os SEIS campos, e não cinco: a razão social morava na
                lista de cima, onde um campo vazio vira o mesmo "—" mudo dos
                opcionais — a pílula contaria um buraco que a lista não mostra. Os seis campos governam dois desfechos (faturar o clube e
                publicar os textos legais) e a ficha é a única tela que os
                cadastra: um clube antigo com buraco precisa anunciar o buraco,
                senão ele só aparece quando alguém tenta salvar e é recusado. */}
            <div style={{ marginTop: space.lg, paddingTop: space.lg, borderTop: `1px solid ${colors.fog}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: space.sm, marginBottom: space.md }}>
                <div style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium }}>Cadastro jurídico</div>
                <LegalIdentityPill missing={legalMissing} />
              </div>
              <dl style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "200px 1fr", rowGap: isMobile ? 4 : 12, columnGap: 16, fontSize: fontSize.md }}>
                <dt style={{ color: colors.stone }}>Razão social</dt><LegalValue value={tenant.legal_name} isMobile={isMobile} />
                <dt style={{ color: colors.stone }}>CNPJ</dt><LegalValue value={tenant.legal_cnpj} isMobile={isMobile} />
                <dt style={{ color: colors.stone }}>Email de cobrança</dt><LegalValue value={tenant.billing_email} isMobile={isMobile} />
                <dt style={{ color: colors.stone }}>Endereço da sede</dt><LegalValue value={tenant.legal_address} isMobile={isMobile} />
                <dt style={{ color: colors.stone }}>Comarca do foro</dt><LegalValue value={tenant.legal_jurisdiction} isMobile={isMobile} />
                <dt style={{ color: colors.stone }}>Encarregado de dados</dt><LegalValue value={tenant.privacy_contact} isMobile={isMobile} last />
              </dl>
            </div>

            <div style={{ marginTop: space.lg, paddingTop: space.lg, borderTop: `1px solid ${colors.fog}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: space.sm }}>
                <div>
                  <div style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, marginBottom: 2 }}>Domínio Vercel</div>
                  <div style={{ fontSize: fontSize.sm, color: colors.stone }}>
                    Reanexa <code style={{ fontFamily: "JetBrains Mono", fontSize: 11 }}>{tenant.slug}.klubora.com</code> ao projeto klubora.
                  </div>
                </div>
                <button onClick={handleRepairDomain} disabled={repairing} style={editButton}>
                  {repairing ? "Reparando..." : "Reparar domínio"}
                </button>
              </div>
              {repairResult && (
                <div style={{
                  marginTop: space.sm,
                  padding: space.sm,
                  borderRadius: radius.sm,
                  background: repairResult.error ? colors.errorSoft : colors.successSoft,
                  color: repairResult.error ? colors.error : colors.success,
                  fontSize: fontSize.sm,
                  fontFamily: "JetBrains Mono",
                  wordBreak: "break-word",
                }}>
                  {repairResult.error
                    ? `Falha: ${repairResult.error}${repairResult.detail ? ` — ${repairResult.detail}` : ""}`
                    : `OK: ${repairResult.host} · ${repairResult.attached === "already" ? "já estava atrelado" : "atrelado agora"} · ${repairResult.attempts} tentativa(s)`}
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <div style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, marginBottom: 12 }}>Lifecycle</div>
          <div style={{ background: colors.white, border: `1px solid ${colors.fog}`, borderRadius: radius.md, boxShadow: shadow.sm, padding: 20 }}>
            <div style={{ borderLeft: `1px solid ${colors.fog}`, paddingLeft: 24, marginLeft: 8 }}>
              {events.map((ev, i) => {
                const isCurrent = i === 0;
                return (
                  <div key={ev.id} style={{ position: "relative", paddingBottom: i === events.length - 1 ? 0 : 24 }}>
                    <div style={{ position: "absolute", left: -29, top: 4, width: 9, height: 9, borderRadius: "50%", background: isCurrent ? colors.success : colors.paper, border: `1.5px solid ${isCurrent ? colors.success : colors.stone}`, boxShadow: isCurrent ? `0 0 0 4px ${colors.successSoft}` : undefined }} />
                    <div style={{ fontSize: fontSize.sm, color: colors.stone, marginBottom: 4 }}>{formatDate(ev.created_at)}</div>
                    <div style={{ fontSize: fontSize.base, fontWeight: fontWeight.medium, marginBottom: 4, display: "flex", alignItems: "center", gap: 8 }}>
                      <Badge state={ev.to_state as never} />
                      {isCurrent && <span style={{ color: colors.stone, fontSize: fontSize.sm, fontWeight: fontWeight.regular }}>· agora</span>}
                    </div>
                    {ev.reason && <div style={{ fontSize: fontSize.md, color: colors.stone }}>{ev.reason}</div>}
                    <div style={{ fontSize: fontSize.sm, color: colors.stoneSoft, marginTop: 4, fontFamily: "JetBrains Mono" }}>{ev.triggered_by}</div>
                  </div>
                );
              })}
              {events.length === 0 && <div style={{ color: colors.stone, fontSize: fontSize.md }}>Nenhum evento registrado.</div>}
            </div>
          </div>
        </div>
      </div>

      {editing && (
        <EditTenantIdentityModal
          tenant={tenant}
          onClose={() => setEditing(false)}
          onSaved={() => refetch()}
        />
      )}
    </>
  );
}

/** Campo vazio se ANUNCIA. Um "—" mudo, igual ao dos campos opcionais, esconde
 *  justamente o que impede de faturar o clube e de publicar os textos legais. */
function LegalValue({ value, isMobile, last }: { value: string | null; isMobile: boolean; last?: boolean }) {
  const filled = typeof value === "string" && value.trim() !== "";
  return (
    <dd style={{ marginBottom: last ? 0 : isMobile ? 8 : 0, color: filled ? undefined : colors.error }}>
      {filled ? value : "— faltando"}
    </dd>
  );
}

const editButton: React.CSSProperties = {
  padding: `${space.xs}px ${space.md}px`,
  border: `1px solid ${colors.fog}`,
  background: colors.white,
  borderRadius: radius.sm,
  color: colors.ink,
  fontSize: fontSize.sm,
  fontWeight: fontWeight.medium,
  cursor: "pointer",
};
